/**
 * KingsBottle — warranty line items
 * ---------------------------------------------------------------------------
 * Adds a warranty service line alongside the physical product, and keeps the
 * two lines in step.
 *
 * Two independent parts:
 *   1. Add to cart — intercepts <product-form> submit and posts BOTH the
 *      physical variant and one warranty variant in a single /cart/add.js
 *      call, so a failure leaves neither line behind.
 *   2. Reconciler — on every cart:change, matches warranty quantities to
 *      their covered lines and removes orphans.
 *
 * theme.js is not modified. Part 1 replicates Prestige's own onSubmit_fn
 * (js/common/product/product-form.js) including its events and loading
 * states; if Prestige is updated, re-check that function against this file.
 */
(function () {
  'use strict';

  var PLAN_PROP = '_warranty_plan';
  var COVERED_ID_PROP = '_covered_variant_id';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var reconciling = false;

  function settings() {
    return (window.themeVariables && window.themeVariables.settings) || {};
  }

  // ---------------------------------------------------------------------
  // Part 1 — add to cart
  // ---------------------------------------------------------------------

  // Capture phase on document fires before Prestige's own bubble-phase
  // listener on the form, so stopImmediatePropagation hands us the submit.
  document.addEventListener('submit', onSubmitCapture, true);

  function onSubmitCapture(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!form.action || form.action.indexOf('/cart/add') === -1) return;

    var picker = form.querySelector('[data-warranty-picker]');
    if (!picker) return; // no warranty on this product — leave Prestige alone

    var selected = picker.querySelector('input[name="warranty_plan"]:checked');
    if (!selected) return; // nothing selected — let Prestige handle it normally

    var warrantyVariantId = selected.getAttribute('data-variant-id');
    if (!warrantyVariantId) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    submitWithWarranty(form, event.submitter, selected, warrantyVariantId);
  }

  async function submitWithWarranty(form, submitter, selected, warrantyVariantId) {
    if (submitter && submitter.getAttribute('aria-busy') === 'true') return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    var submitButtons = Array.from(form.elements).filter(function (el) {
      return el.type === 'submit';
    });
    submitButtons.forEach(function (b) { b.setAttribute('aria-busy', 'true'); });

    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:start', { bubbles: true })
    );

    var sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    var formData = new FormData(form);
    var productVariantId = formData.get('id');
    var quantity = parseInt(formData.get('quantity'), 10) || 1;
    var onSuccessDo = formData.get('on_success');
    var plan = selected.value; // 2Y | 3Y | 5Y

    var productLine = {
      id: productVariantId,
      quantity: quantity,
      properties: {}
    };
    productLine.properties[PLAN_PROP] = plan;

    // Any properties[...] inputs already on the form (gift card recipient,
    // engraving, etc.) must survive.
    formData.forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') productLine.properties[match[1]] = value;
    });

    var warrantyLine = {
      id: warrantyVariantId,
      quantity: quantity,
      properties: {
        'Covered product': picker(form).getAttribute('data-product-title') || '',
        'Covered SKU': selectedSku(form),
        'Total warranty coverage': selected.getAttribute('data-coverage') || ''
      }
    };
    warrantyLine.properties[COVERED_ID_PROP] = String(productVariantId);

    var body = {
      // Shopify prepends each added line, so the LAST item in this array ends
      // up at the top of the cart. Warranty first here means the covered
      // product renders above its warranty line.
      items: [warrantyLine, productLine],
      sections: sectionsToBundle.join(','),
      sections_url: root + 'variants/' + productVariantId
    };

    var response;
    try {
      response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest' // needed for inventory checks
        },
        body: JSON.stringify(body)
      });
    } catch (e) {
      submitButtons.forEach(function (b) { b.removeAttribute('aria-busy'); });
      document.documentElement.dispatchEvent(
        new CustomEvent('theme:loading:end', { bubbles: true })
      );
      form.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: String(e && e.message ? e.message : e) }
        })
      );
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return;
    }

    submitButtons.forEach(function (b) { b.removeAttribute('aria-busy'); });
    var responseJson = await response.json();
    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:end', { bubbles: true })
    );

    if (!response.ok) {
      form.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: responseJson['description'] }
        })
      );
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return;
    }

    if (settings().cartType === 'page' || settings().pageType === 'cart') {
      window.location.href = root + 'cart';
      return;
    }

    var cartContent = await (await fetch(root + 'cart.js')).json();
    cartContent['sections'] = responseJson['sections'];

    form.dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: responseJson.hasOwnProperty('items')
            ? responseJson['items']
            : [responseJson],
          cart: cartContent,
          onSuccessDo: onSuccessDo
        }
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          onSuccessDo: onSuccessDo,
          cart: cartContent
        }
      })
    );
  }

  function picker(form) {
    return form.querySelector('[data-warranty-picker]');
  }

  function selectedSku(form) {
    var p = picker(form);
    if (!p) return '';
    var id = String(new FormData(form).get('id'));
    var map = p.getAttribute('data-variant-skus');
    if (!map) return '';
    try {
      return JSON.parse(map)[id] || '';
    } catch (e) {
      return '';
    }
  }

  // ---------------------------------------------------------------------
  // Part 2 — reconciler
  // ---------------------------------------------------------------------

  document.documentElement.addEventListener('cart:change', function (event) {
    var cart = event.detail && event.detail.cart;
    if (cart) reconcile(cart);
    else fetchAndReconcile();
  });

  document.addEventListener('DOMContentLoaded', fetchAndReconcile);

  async function fetchAndReconcile() {
    try {
      var cart = await (await fetch(root + 'cart.js')).json();
      reconcile(cart);
    } catch (e) {
      /* a failed reconcile must never break the page */
    }
  }

  async function reconcile(cart) {
    if (reconciling || !cart || !cart.items) return;

    // What each covered variant should have, keyed by "variantId|plan"
    var covered = {};
    cart.items.forEach(function (item) {
      var props = item.properties || {};
      if (props[COVERED_ID_PROP]) return; // this is a warranty line
      var plan = props[PLAN_PROP];
      if (!plan) return; // not a warranty-bearing product
      covered[item.variant_id + '|' + plan] = item.quantity;
    });

    var updates = {};
    cart.items.forEach(function (item) {
      var props = item.properties || {};
      var coveredId = props[COVERED_ID_PROP];
      if (!coveredId) return; // not a warranty line

      var plan = props[PLAN_PROP] || planFromCoverage(props['Total warranty coverage']);
      var want = covered[coveredId + '|' + plan];

      if (want === undefined) {
        updates[item.key] = 0; // orphan — the covered line is gone
      } else if (want !== item.quantity) {
        updates[item.key] = want; // quantity drifted
      }
    });

    if (Object.keys(updates).length === 0) return;

    reconciling = true;
    try {
      var sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      var response = await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: updates,
          sections: sectionsToBundle.join(',')
        })
      });
      if (!response.ok) return;

      if (settings().pageType === 'cart') {
        window.location.reload();
        return;
      }

      var updated = await response.json();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'line-item:change', cart: updated }
        })
      );
    } finally {
      reconciling = false;
    }
  }

  function planFromCoverage(text) {
    if (!text) return '';
    var m = String(text).match(/(\d+)/);
    return m ? m[1] + 'Y' : '';
  }
})();
